import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
} from '@travellegacy/nexops-design-system';
import { Loader2, Pencil, Plus, Trash2, X, Check } from 'lucide-react';
import { employeeAdditionalsAPI } from '@/shared/lib/api';
import { formatCurrency } from '@/shared/lib/utils';
import { toast } from '@/shared/lib/toast';

interface EmployeeAdditional {
  id: string;
  employeeId: string;
  month: number;
  year: number;
  amount: number;
  description: string;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  employee: { id: string; name: string } | null;
  month: number;
  year: number;
  isMonthClosed: boolean;
  /** VT pago no Pix (automático pelo cadastro do colaborador) */
  vtPixAmount: number;
}

const MONTHS = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

/** "12345" → "123,45" (digitação de moeda em centavos) */
function maskCurrency(value: string): string {
  const digits = value.replace(/\D/g, '');
  if (!digits) return '';
  return (parseInt(digits, 10) / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function parseCurrency(value: string): number {
  if (!value) return 0;
  return parseFloat(value.replace(/\./g, '').replace(',', '.')) || 0;
}

function toCurrencyInput(amount: number): string {
  return amount.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function apiError(error: unknown, fallback: string): string {
  const data = (error as { response?: { data?: { error?: string | { message?: string } } } })?.response?.data;
  const err = data?.error;
  if (typeof err === 'string') return err;
  return err?.message || fallback;
}

/**
 * Adicionais a pagar lançados à mão para um colaborador no mês (ex.: comissão das torres por erro).
 * Somam no líquido como as comissões e, para CLT, entram na base do INSS e do FGTS.
 */
export function EmployeeAdditionalsDialog({ open, onOpenChange, employee, month, year, isMonthClosed, vtPixAmount }: Props) {
  const queryClient = useQueryClient();
  const [amountDisplay, setAmountDisplay] = useState('');
  const [description, setDescription] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editAmount, setEditAmount] = useState('');
  const [editDescription, setEditDescription] = useState('');

  const queryKey = ['employee-additionals', employee?.id, month, year];
  const { data: additionals = [], isLoading } = useQuery({
    queryKey,
    queryFn: async () => {
      const response = await employeeAdditionalsAPI.list(month, year, employee!.id);
      return response.data.data as EmployeeAdditional[];
    },
    enabled: open && !!employee,
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey });
    queryClient.invalidateQueries({ queryKey: ['employee-payments', month, year] });
  };

  const createMutation = useMutation({
    mutationFn: () =>
      employeeAdditionalsAPI.create({
        employeeId: employee!.id,
        month,
        year,
        amount: parseCurrency(amountDisplay),
        description: description.trim(),
      }),
    onSuccess: () => {
      setAmountDisplay('');
      setDescription('');
      refresh();
      toast.success('Adicional lançado');
    },
    onError: (error) => toast.error(apiError(error, 'Erro ao lançar adicional')),
  });

  const updateMutation = useMutation({
    mutationFn: (id: string) =>
      employeeAdditionalsAPI.update(id, { amount: parseCurrency(editAmount), description: editDescription.trim() }),
    onSuccess: () => {
      setEditingId(null);
      refresh();
      toast.success('Adicional atualizado');
    },
    onError: (error) => toast.error(apiError(error, 'Erro ao editar adicional')),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => employeeAdditionalsAPI.remove(id),
    onSuccess: () => {
      refresh();
      toast.success('Adicional excluído');
    },
    onError: (error) => toast.error(apiError(error, 'Erro ao excluir adicional')),
  });

  const total = additionals.reduce((sum, a) => sum + a.amount, 0) + vtPixAmount;
  const canCreate = parseCurrency(amountDisplay) > 0 && description.trim().length > 0 && !createMutation.isPending;

  const startEdit = (a: EmployeeAdditional) => {
    setEditingId(a.id);
    setEditAmount(toCurrencyInput(a.amount));
    setEditDescription(a.description);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        if (!value) setEditingId(null);
        onOpenChange(value);
      }}
    >
      <DialogContent className="sm:max-w-lg px-6 pt-6 pb-6">
        <DialogHeader>
          <DialogTitle>Adicionais de {employee?.name}</DialogTitle>
          <DialogDescription>
            {MONTHS[month - 1]}/{year}. Valores somados ao pagamento do mês, como as comissões.
            Para CLT, entram na base do INSS e do FGTS.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {isLoading ? (
            <div className="flex justify-center py-6">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
            </div>
          ) : additionals.length === 0 && !vtPixAmount ? (
            <p className="text-sm text-muted-foreground py-2">Nenhum adicional lançado neste mês.</p>
          ) : (
            <ul className="divide-y rounded-md border">
              {vtPixAmount > 0 && (
                <li className="flex items-center gap-3 p-3">
                  <span className="flex-1 text-sm">
                    Vale Transporte (Pix)
                    <span className="block text-xs text-muted-foreground">
                      Automático pelo cadastro do colaborador. Sem INSS/FGTS.
                    </span>
                  </span>
                  <span className="text-sm font-medium tabular-nums">{formatCurrency(vtPixAmount)}</span>
                </li>
              )}
              {additionals.map((a) =>
                editingId === a.id ? (
                  <li key={a.id} className="p-3 space-y-2">
                    <Input
                      value={editDescription}
                      onChange={(e) => setEditDescription(e.target.value)}
                      placeholder="Descrição"
                      maxLength={500}
                    />
                    <div className="flex items-center gap-2">
                      <div className="relative flex-1">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">R$</span>
                        <Input
                          className="pl-10"
                          inputMode="numeric"
                          value={editAmount}
                          onChange={(e) => setEditAmount(maskCurrency(e.target.value))}
                        />
                      </div>
                      <Button
                        size="sm"
                        onClick={() => updateMutation.mutate(a.id)}
                        disabled={updateMutation.isPending || parseCurrency(editAmount) <= 0 || !editDescription.trim()}
                        aria-label="Salvar"
                      >
                        <Check className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setEditingId(null)} aria-label="Cancelar">
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </li>
                ) : (
                  <li key={a.id} className="flex items-center gap-3 p-3">
                    <span className="flex-1 text-sm break-words">{a.description}</span>
                    <span className="text-sm font-medium tabular-nums">{formatCurrency(a.amount)}</span>
                    {!isMonthClosed && (
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => startEdit(a)} aria-label="Editar">
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => deleteMutation.mutate(a.id)}
                          disabled={deleteMutation.isPending}
                          aria-label="Excluir"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </li>
                )
              )}
              <li className="flex items-center justify-between p-3 bg-muted/40">
                <span className="text-sm font-semibold">Total</span>
                <span className="text-sm font-semibold tabular-nums">{formatCurrency(total)}</span>
              </li>
            </ul>
          )}

          {isMonthClosed ? (
            <p className="text-sm text-muted-foreground">
              Mês fechado: os adicionais não podem ser alterados. Para mudar, reabra o mês no Financeiro.
            </p>
          ) : (
            <form
              className="space-y-3 border-t pt-4"
              onSubmit={(e) => {
                e.preventDefault();
                if (canCreate) createMutation.mutate();
              }}
            >
              <p className="text-sm font-medium">Novo adicional</p>
              <Input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Descrição (ex.: Torre: 3 erros encontrados)"
                maxLength={500}
              />
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">R$</span>
                  <Input
                    className="pl-10"
                    inputMode="numeric"
                    placeholder="0,00"
                    value={amountDisplay}
                    onChange={(e) => setAmountDisplay(maskCurrency(e.target.value))}
                  />
                </div>
                <Button type="submit" disabled={!canCreate}>
                  {createMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
                  Adicionar
                </Button>
              </div>
            </form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
